import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getListingOffers, acceptOffer, rejectOffer } from '../services/offerService';
import '../styles/OfferList.css';

const OfferList = ({ listingId }) => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('price'); // 'price' or 'date'
    const [filterBy, setFilterBy] = useState('all'); // 'all', 'pending', 'accepted', 'rejected'

    useEffect(() => {
        loadOffers();
    }, [listingId]);

    const loadOffers = async () => {
        try {
            setLoading(true);
            const data = await getListingOffers(listingId);
            setOffers(data);
            setError(null);
        } catch (err) {
            setError('Failed to load offers');
            console.error('Error loading offers:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptOffer = async (offerId) => {
        try {
            await acceptOffer(offerId);
            await loadOffers(); // Reload offers after accepting
        } catch (err) {
            setError('Failed to accept offer');
            console.error('Error accepting offer:', err);
        }
    };

    const handleRejectOffer = async (offerId) => {
        try {
            await rejectOffer(offerId);
            await loadOffers(); // Reload offers after rejecting
        } catch (err) {
            setError('Failed to reject offer');
            console.error('Error rejecting offer:', err);
        }
    };

    const sortOffers = (offers) => {
        return [...offers].sort((a, b) => {
            if (sortBy === 'price') {
                return b.price_offer - a.price_offer;
            } else {
                return new Date(b.created_at) - new Date(a.created_at);
            }
        });
    };

    const filterOffers = (offers) => {
        if (filterBy === 'all') return offers;
        return offers.filter(offer => offer.status === filterBy);
    };

    if (loading) return <div className="loading">Loading offers...</div>;
    if (error) return <div className="error">{error}</div>;
    if (offers.length === 0) return <div className="no-offers">No offers yet</div>;

    const sortedAndFilteredOffers = filterOffers(sortOffers(offers));

    return (
        <div className="offer-list">
            <div className="offer-controls">
                <div className="sort-controls">
                    <label>Sort by:</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="price">Price (Highest First)</option>
                        <option value="date">Date (Newest First)</option>
                    </select>
                </div>
                <div className="filter-controls">
                    <label>Filter by:</label>
                    <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
                        <option value="all">All Offers</option>
                        <option value="pending">Pending</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
            </div>

            <div className="offers-grid">
                {sortedAndFilteredOffers.map(offer => (
                    <div key={offer.id} className={`offer-card ${offer.status}`}>
                        <div className="offer-header">
                            <h3>Offer from {offer.buyer_name}</h3>
                            <span className={`status ${offer.status}`}>{offer.status}</span>
                        </div>
                        <div className="offer-details">
                            <p className="price">${offer.price_offer}</p>
                            <p className="delivery">
                                <strong>Delivery:</strong> {offer.delivery_method}
                            </p>
                            <p className="date">
                                <strong>Available:</strong> {new Date(offer.date_available).toLocaleDateString()}
                            </p>
                            <p className="created">
                                <strong>Offered:</strong> {new Date(offer.created_at).toLocaleDateString()}
                            </p>
                        </div>
                        {offer.status === 'pending' && (
                            <div className="offer-actions">
                                <button 
                                    className="accept-btn"
                                    onClick={() => handleAcceptOffer(offer.id)}
                                >
                                    Accept
                                </button>
                                <button 
                                    className="reject-btn"
                                    onClick={() => handleRejectOffer(offer.id)}
                                >
                                    Reject
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OfferList; 